import { Injectable } from '@angular/core';

import { Subject, BehaviorSubject, Observable, from } from 'rxjs';
import { concatMap, takeUntil, tap, filter } from 'rxjs/operators';

import cloneDeep from 'lodash/fp/cloneDeep';

import {
    ApiService,
    AuthService,
    SocketService,
    LocalStorageService,
    ConstantsService
} from './core/upgraded-providers';

import {
    VersionService
} from './version.service';

export const SESSION_SERVER = 'session.server';

let constants = null;
@Injectable()
export class SessionService {
    _destroySbj: Subject<void> = new Subject();

    _serverSbj: BehaviorSubject<IServer> = new BehaviorSubject(null);
    server$: Observable<IServer> = this._serverSbj.asObservable();
    set server(server: IServer) {
        localStorage.setItem(SESSION_SERVER, JSON.stringify(server));

        this._serverSbj.next(server);
    }
    get server(): IServer {
        return this._serverSbj.getValue();
    }

    constructor(
        private _apiSrv: ApiService,
        private _authSrv: AuthService,
        private _constantsSrv: ConstantsService,
        private _socketSrv: SocketService,
        private _localStorageSrv: LocalStorageService,
        private _versionSrv: VersionService
    ) {
        try {
            const server = JSON.parse(localStorage.getItem(SESSION_SERVER));

            this._serverSbj.next(server);
        } catch (err) {
            // Noop.
        }

        if (!constants) {
            constants = cloneDeep(this._constantsSrv);
        }

        this._versionSrv.data$
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap(() => {
                mergeDeep(this._constantsSrv, constants);
            }))
            .pipe(concatMap(({ serverData: { features } }) => {
                return from(features);
            }))
            .pipe(filter(({ constants }) => !!constants))
            .pipe(tap(({ constants }) => {
                mergeDeep(this._constantsSrv, constants);
            }))
            .subscribe();
    }

    reset() {
    }

    async initSessionServer(server: IServer, versionData: IVersionData = null) {
        this.server = server;

        if (!versionData) {
            versionData = await this._versionSrv.getData(server.apiUrl);
        }

        this._versionSrv.data = versionData;

        this._apiSrv.options.official = false;
        Object.assign(this._apiSrv.options, server.settings);
        this._apiSrv.options.serverData = versionData.serverData;
        this._apiSrv.options.apiUrl = server.apiUrl;
        if (!this._apiSrv.options.serverData.historyChunkSize) {
            this._apiSrv.options.serverData.historyChunkSize = 20;
        }
        // if (!this._apiService.options.serverData.shards) {
        //     this._apiService.options.serverData.shards = ['shard0'];
        // }
        this._localStorageSrv.prefix = server.prefix;

        this._socketSrv.options.websocketUrl = server.websocketUrl;
        this._socketSrv.reconnect();

        this._authSrv.useNativeAuth = this._versionSrv.data.useNativeAuth;
    }

    _dispose() {
        this._destroySbj.next();
        this._destroySbj.complete();
    }
}

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
*/
export function isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
}
  
/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
*/
export function mergeDeep(target, ...sources) {
    if (!sources.length) {
        return target;
    }

    const source = sources.shift();
  
    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }

    // @ts-ignore
    return mergeDeep(target, ...sources);
}
